import { defineStore } from 'pinia';
import type {
  ShippingLockers,
  ShippingMethod,
} from '~/repository/modules/ShippingMethod.module';
import {
  CARD_PAYMENT_METHOD,
  KLARNA_PARTPAYMENT_IDENTIFIER,
  PAYMENT_METHODS_THAT_ARE_MONTHLY_PAYMENT,
  SWISH_METHOD,
} from '~/models/checkout/PaymentMethods.model';
import type { ShoppingCart } from '~/models/shopping_cart/ShoppingCart.model';
import type {
  AppliedDiscount,
  NumberSelectionModel,
} from '~/models/shopping_cart/ShoppingCart.api.model';
import type { CustomerConsentDto } from '~/models/customer/CustomerConsent.api.model';
import { APP_CART_DATA_KEY } from '~/models/AsyncDataKeys.model';
import type { ShoppingCartSubTotalsModel } from '~/models/shopping_cart/ShoppingCartSubTotals.api.model';
import {
  defaultCheckoutIndicatorSteps,
  checkoutIndicatorStepsNoNumber,
  CHECKOUT_STEP_1_NAME,
} from '~/models/checkout/CheckoutSteps.model';
import type { StepIndicatorStep } from '~/components/StepIndicator.vue';
import { Category } from '~/models/Category.models';
import type { NumberConfigByIds } from '~/utils/checkout/checkoutStore/numberSelectionMap';
import { KEEP_NUMBER_OPTION__NEW } from '~/models/checkout/checkout_store/NumberConfigCheckoutState.model';
import type { ShoppingCartItemModel } from '~/models/shopping_cart/ShoppingCartItem.api.model';
import type { HalebopConfirmOrderRequest } from '~/models/checkout/ConfirmOrderRequest.api.model';
import type { DeliveryAddress } from '~/models/DeliveryAddress.model';
import { checkout_sendCheckoutLogToBackend } from '~/utils/checkout/sendCheckoutLogToBackend';

type CartProductPricesByItemIdAndDownPayment = {
  [key: string]: string;
};

type ShoppingCartDataState = {
  cartItems: ShoppingCartItemModel[];
  subTotals: ShoppingCartSubTotalsModel;
  appliedDiscounts: AppliedDiscount[];
  appliedDiscountsCouponCodes: string[];
};

export type FetchedData = {
  _teliaPayToken?: string;
  address?: DeliveryAddress;
  buybackIsEnabled?: boolean;
  selectedDownPayment?: number;
  cart: ShoppingCartDataState;
  confirmedOrderId?: number;
  numberConfigSections?: NumberSelectionModel[];
  ssn?: string;
  ssnCensored?: string;
  shippingOptions: ShippingMethod[];
  email?: string;
};

type PaymentState = {
  selectedPaymentMethodSystemName?: string;
  selectedPaymentMethodCleanName?: string;
  selectedPaymentMethodCommaSeparatedIdentifier?: string;
  selectedDownPayTime: number;
  inputtedSwishPhoneNumber: string;
  initialDownpayTime: number;
};

export type UserState = {
  discountCode: string;
  numberConfigMap: NumberConfigByIds;
  newEmailAddress: string;
  payment: PaymentState;
  selectedDeliveryMethod?: ShippingMethod;
  inputtedInstaboxPhoneNumber: string;
};

type UI = {
  bankIdTestPassthroughIsSubmitting: boolean;
  cartAccordionIsOpen: boolean;
  currentStep: number;
  customerRequiresAuth: boolean;
  loading: {
    isStep3AddressLoading: boolean;
    isStep3ShippingLoading: boolean;
    isStep3SsnCensoredLoading: boolean;
    isStep4TeliaPayInitiated: boolean;
    isStep4KlarnaMethodsLoading: boolean;
  };
  numberConfigFormIsSubmitting: boolean;
  _steps: StepIndicatorStep[];
  teliaPayReady: boolean;
  klarnaReady: boolean;
  klarnaDeniedUserOrAvailability: boolean;
  _cartProductPricesByItemIdAndDownPayment: CartProductPricesByItemIdAndDownPayment;
  bindingPeriodCheckHasActiveBinding: boolean | null;
};

type CheckoutStoreState = {
  customerConsents: CustomerConsentDto[];
  fetchedData: FetchedData;
  ui: UI;
  userState: UserState;
};

export type TrackingData = {
  ecommerce: {
    items: object[];
  };
  selectedShipping?: string;
  selectedPayment: string;
  numberOptions: object;
};

export const useMyCheckoutStore = defineStore('myCheckoutStore', {
  state: (): CheckoutStoreState => ({
    customerConsents: [],
    fetchedData: {
      _teliaPayToken: '',
      address: undefined,
      buybackIsEnabled: true,
      cart: {
        cartItems: [],
        subTotals: {} as ShoppingCartSubTotalsModel,
        appliedDiscounts: [],
        appliedDiscountsCouponCodes: [],
      },
      confirmedOrderId: undefined,
      numberConfigSections: [],
      ssnCensored: '',
      shippingOptions: [],
      selectedDownPayment: 36,
    },
    userState: {
      discountCode: '',
      numberConfigMap: {},
      newEmailAddress: '',
      payment: {
        selectedDownPayTime: 36,
        selectedPaymentMethodSystemName: undefined,
        selectedPaymentMethodCleanName: undefined,
        selectedPaymentMethodCommaSeparatedIdentifier: undefined,
        inputtedSwishPhoneNumber: '',
        initialDownpayTime: 36,
      },
      selectedDeliveryMethod: undefined,
      inputtedInstaboxPhoneNumber: '',
    },
    ui: {
      bankIdTestPassthroughIsSubmitting: false,
      cartAccordionIsOpen: true,
      _cartProductPricesByItemIdAndDownPayment: {},
      currentStep: 1,
      customerRequiresAuth: true,
      klarnaReady: false,
      klarnaDeniedUserOrAvailability: false,
      loading: {
        isStep3AddressLoading: false,
        isStep3ShippingLoading: false,
        isStep3SsnCensoredLoading: false,
        isStep4TeliaPayInitiated: false,
        isStep4KlarnaMethodsLoading: false,
      },
      numberConfigFormIsSubmitting: false,
      _steps: defaultCheckoutIndicatorSteps,
      teliaPayReady: false,
      bindingPeriodCheckHasActiveBinding: null,
    },
  }),
  actions: {
    async fetchCart() {
      this.clearCartData();

      const { $api } = useNuxtApp();
      const shoppingCart =
        await $api.ShoppingCartModule.GetShoppingCart('ShoppingCart');
      if (shoppingCart) {
        this.populateCartState(shoppingCart);
      } else {
        this.clearCartData();
      }
      return shoppingCart;
    },
    async refetchCartForUpdatedDiscounts() {
      const { $api } = useNuxtApp();
      const shoppingCart =
        await $api.ShoppingCartModule.GetShoppingCart('ShoppingCart');

      if (shoppingCart) {
        this.updateCartItemsAndDiscounts(shoppingCart);
      }

      return shoppingCart;
    },
    async refreshCartAsyncDataHook() {
      refreshNuxtData(APP_CART_DATA_KEY);
    },
    async finalizeCheckout(): Promise<string> {
      const { $api } = useNuxtApp();

      checkout_sendCheckoutLogToBackend(
        'Shop',
        'Finalize Checkout FE - 1',
        `Finalize Checkout init`,
      );

      if (!this.userState.payment.selectedPaymentMethodSystemName) {
        checkout_sendCheckoutLogToBackend(
          'Shop',
          'Finalize Checkout FE - Error',
          `Finalize Checkout Error: Cannot get selected payment method system names`,
        );
        throw new Error();
      }

      const swishNumber =
        this.userState.payment.selectedPaymentMethodSystemName === SWISH_METHOD
          ? this.userState.payment.inputtedSwishPhoneNumber
          : undefined;

      await $api.PaymentModule.selectPaymentMethod(
        this.userState.payment.selectedPaymentMethodSystemName,
        swishNumber,
      );

      checkout_sendCheckoutLogToBackend(
        'Shop',
        'Finalize Checkout FE - 2',
        `Selected payment method`,
      );

      await $api.PaymentModule.getPaymentInfo();

      checkout_sendCheckoutLogToBackend(
        'Shop',
        'Finalize Checkout FE - 3',
        `Fetched payment info`,
      );

      const confirmedOrder = await $api.checkout.confirmOrder(
        this.confirmOrderRequestPayload,
      );

      checkout_sendCheckoutLogToBackend(
        'Shop',
        'Finalize Checkout FE - 4',
        `Confirm order. OrderId: ${confirmedOrder.Id}`,
      );

      const orderId = confirmedOrder?.Id;
      const orderGuid = confirmedOrder?.OrderGuid;

      if (
        this.userState.payment.selectedPaymentMethodSystemName ===
        CARD_PAYMENT_METHOD
      ) {
        const paymentInfoWebViewResponse =
          await $api.PaymentModule.getPaymentInfoWebView(orderId);

        // Netaxept card payment
        if (
          checkoutStore_isPaymentInfoResponseEPaymentsRedirect(
            paymentInfoWebViewResponse,
          )
        ) {
          const cardPaymentRedirectUrl =
            checkoutStore_formatEPaymentRedirectUrl(paymentInfoWebViewResponse);

          if (cardPaymentRedirectUrl) {
            return cardPaymentRedirectUrl;
          }
        }
      }

      // Other payment methods redirect
      if (orderGuid) {
        checkout_sendCheckoutLogToBackend(
          'Shop',
          'Finalize Checkout FE - Final',
          `Order creation successful. Redirect to receipt`,
        );
        return `/shop/kvitto/${orderGuid.replaceAll('-', '')}`;
      }

      checkout_sendCheckoutLogToBackend(
        'Shop',
        'Finalize Checkout FE - Final',
        `Order creation failed.`,
      );

      const notification = useNotificationStore();
      notification.setStandardErrorNotification();
      return '';
    },
    populateCartState(shoppingCart: ShoppingCart) {
      this.fetchedData.cart = {
        appliedDiscounts: shoppingCart.AppliedDiscounts,
        appliedDiscountsCouponCodes: shoppingCart.DiscountCouponCodes,
        cartItems: shoppingCart.CartItems || [],
        subTotals: shoppingCart.SubTotals,
      };
      this.fetchedData.buybackIsEnabled = shoppingCart.EnableBuyback;
      this.fetchedData.selectedDownPayment = shoppingCart.SelectedDownPayments;
      this.userState.payment.selectedDownPayTime =
        shoppingCart.SelectedDownPayments;
      const lastOption =
        shoppingCart.SubTotals.Payments.findLast((x) => x.DownPayments !== 1)
          ?.DownPayments ?? 1;
      const downPayment =
        shoppingCart.SubTotals.Payments.find(
          (x) => x.DownPayments == shoppingCart.SelectedDownPayments,
        )?.DownPayments ?? 1;
      const downPaymentTime =
        lastOption < downPayment || downPayment === 1
          ? lastOption
          : downPayment;
      this.userState.payment.initialDownpayTime = downPaymentTime ?? 1;

      if (shoppingCart.NumberSelections) {
        this.fetchedData.numberConfigSections =
          shoppingCart.NumberSelections.slice();
      }

      this.ui.customerRequiresAuth = shoppingCart.CustomerRequiresAuth ?? true;
      const cartHasAnySubscriptions = shoppingCart.CartItems?.find(
        (item) =>
          item.Category === Category.postPaid ||
          item.Category === Category.prePaid ||
          !!item.BundlePlanItem?.DataAllowance?.DataSize,
      );

      this.ui._steps = cartHasAnySubscriptions
        ? defaultCheckoutIndicatorSteps
        : checkoutIndicatorStepsNoNumber;

      const newNumberConfigByBundleIdAndItemIdMap = checkoutStore_createMap(
        shoppingCart.NumberSelections,
        shoppingCart.CartItems,
      );

      this.userState.numberConfigMap = newNumberConfigByBundleIdAndItemIdMap;

      this.makeNewCartProductPricesByItemIdAndDownPayment(
        shoppingCart.CartItems,
      );
    },
    updateCartItemsAndDiscounts(shoppingCart: ShoppingCart): void {
      const notificationStore = useNotificationStore();

      const amountAppliedDiscountsBefore =
        this.fetchedData.cart.appliedDiscounts.length;

      const amountAppliedDiscountsAfter = shoppingCart.AppliedDiscounts
        ? shoppingCart.AppliedDiscounts.length
        : 0;

      this.fetchedData.cart = {
        appliedDiscounts: shoppingCart.AppliedDiscounts || [],
        appliedDiscountsCouponCodes: shoppingCart.DiscountCouponCodes || [],
        cartItems: shoppingCart.CartItems || [],
        subTotals: shoppingCart.SubTotals,
      };

      this.makeNewCartProductPricesByItemIdAndDownPayment(
        shoppingCart.CartItems,
      );

      // One or more discounts got removed
      if (
        amountAppliedDiscountsBefore > amountAppliedDiscountsAfter &&
        this.ui.bindingPeriodCheckHasActiveBinding !== true
      ) {
        notificationStore.setNewNotification({
          type: 'warning',
          isCloseable: true,
          link: {
            url: 'https://www.halebop.se/support',
            text: 'kontakta supporten',
          },
          title: 'Rabatt har tagits bort.',
          msg: 'En eller flera rabatter kunde inte kombineras i varukorgen och har därför tagits bort. Det kan till exempel bero på att rabatten endast gällde antingen nya kunder eller befintliga kunder.',
        });
      }
    },
    makeNewCartProductPricesByItemIdAndDownPayment(
      cartItems: ShoppingCartItemModel[],
    ): void {
      const newCartProductPricesByItemIdAndDownPayment: CartProductPricesByItemIdAndDownPayment =
        {};

      for (const item of cartItems) {
        if (item.Payments) {
          for (const payment of item.Payments) {
            const key = checkoutStore_makeKeyForCartProductPriceMap(
              item.Id,
              payment.DownPayments,
            );

            let priceString: string = '';
            if (payment) {
              priceString = checkoutStore_makePriceStringForCartProductPriceMap(
                payment.ProductDownPayment.toLocaleString('sv-SE') ?? '0',
                payment.ProductPaymentUnit ?? 'kr',
              );
            }
            newCartProductPricesByItemIdAndDownPayment[key] = priceString;
          }
        }
      }

      this.ui._cartProductPricesByItemIdAndDownPayment =
        newCartProductPricesByItemIdAndDownPayment;
    },
    makeBuybackItemPriceStringToDisplay(item: ShoppingCartItemModel): string {
      const priceReduction = Array.isArray(item.PaymentReductions)
        ? item.PaymentReductions.find(
            (payment: {
              DownPayments: number;
              DownPaymentMonthlyReduction?: string;
            }) =>
              payment.DownPayments ===
              this.userState.payment.selectedDownPayTime,
          )?.DownPaymentMonthlyReduction
        : undefined;

      const dataReduction =
        (item.AdditionalDataAllowance?.DataSize ?? '') +
        (item.AdditionalDataAllowance?.DataUnit ?? '');

      const priceReductionUnit =
        this.userState.payment.selectedDownPayTime > 1 ? 'kr/mån' : 'kr';

      return priceReduction
        ? `-${priceReduction} ${priceReductionUnit}`
        : `+${dataReduction}/mån`;
    },
    makeCartProductItemPriceStringToDisplay(itemId: number): string {
      const paymentMethodName =
        this.userState.payment.selectedPaymentMethodCommaSeparatedIdentifier;

      // Show monthly payment price string if no payment option not selected
      // (show montly payment by default)
      // Show direct payment if payment option selected and it is not a monthly payment method
      let shouldShowDirectPaymentPrice = false;

      if (paymentMethodName) {
        shouldShowDirectPaymentPrice =
          !PAYMENT_METHODS_THAT_ARE_MONTHLY_PAYMENT.includes(paymentMethodName);
      } else if (
        this.userState.payment.selectedDownPayTime &&
        this.userState.payment.selectedDownPayTime === 1
      ) {
        shouldShowDirectPaymentPrice = true;
      } else if (
        !this.fetchedData.cart.cartItems?.some(
          (x) => x.Category !== 'Tillbehör',
        )
      ) {
        shouldShowDirectPaymentPrice = true;
        this.userState.payment.selectedDownPayTime = 1;
      } else {
        shouldShowDirectPaymentPrice = false;
      }

      const downPayTime = shouldShowDirectPaymentPrice
        ? 1
        : this.userState.payment.selectedDownPayTime;

      const key = checkoutStore_makeKeyForCartProductPriceMap(
        itemId,
        downPayTime,
      );
      return this.ui._cartProductPricesByItemIdAndDownPayment[key] ?? '';
    },
    clearCartData() {
      this.fetchedData.cart = {
        appliedDiscounts: [],
        appliedDiscountsCouponCodes: [],
        cartItems: [],
        subTotals: {} as ShoppingCartSubTotalsModel,
      };
      this.ui._cartProductPricesByItemIdAndDownPayment = {};
      this.ui.currentStep = 1;
    },
    goToNextStep() {
      this.ui.currentStep = this.ui.currentStep + 1;
    },
    async checkActiveBindingPeriod() {
      const { $api } = useNuxtApp();
      this.ui.bindingPeriodCheckHasActiveBinding = null;

      const msisdns = Object.values(this.userState.numberConfigMap)
        .map((config) => config.keepNumberInputValue)
        .filter((val) => !!val);

      if (msisdns.length === 0) {
        this.ui.bindingPeriodCheckHasActiveBinding = false;
        return;
      }

      const results = await Promise.allSettled(
        msisdns.map((msisdn) => $api.CustomerModule.CheckBindingPeriod(msisdn)),
      );

      const hasActiveBinding = results.some(
        (result) =>
          result.status === 'fulfilled' && result.value.AllowRenewal === false,
      );

      this.ui.bindingPeriodCheckHasActiveBinding = hasActiveBinding;
    },
  },
  getters: {
    isStep2ProceedBlocked(): boolean {
      if (this.ui.bindingPeriodCheckHasActiveBinding === true) {
        return true;
      }
      return Object.values(this.userState.numberConfigMap).some(
        (config) =>
          config.keepNumberOption !== KEEP_NUMBER_OPTION__NEW &&
          !config.keepNumberCodeValidated,
      );
    },
    isStep3ProceedBlocked(): boolean {
      return this.ui.bindingPeriodCheckHasActiveBinding === true;
    },
    isStep4ProceedBlocked(): boolean {
      return this.ui.bindingPeriodCheckHasActiveBinding === true;
    },
    cartContainsBundle: (state: CheckoutStoreState) => {
      return state.fetchedData.cart?.cartItems.some(
        (item: ShoppingCartItemModel) => item.BundleId,
      );
    },
    cartContainsCellphone: (state: CheckoutStoreState) => {
      return state.fetchedData.cart?.cartItems.some(
        (item: ShoppingCartItemModel) => item.Category === Category.phones,
      );
    },
    cartContainsSubsciptionsOnly: (state: CheckoutStoreState) => {
      return state.fetchedData.cart?.cartItems.reduce(
        (onlySubscription: boolean, item: ShoppingCartItemModel) =>
          onlySubscription &&
          (item.Category === Category.postPaid ||
            item.Category === Category.prePaid),
        true,
      );
    },
    confirmOrderRequestPayload: (
      state: CheckoutStoreState,
    ): HalebopConfirmOrderRequest =>
      checkoutStore_createConfirmOrderPayload(
        state.customerConsents,
        state.userState,
        state.fetchedData,
      ),
    currentCheckoutStepName: (state: CheckoutStoreState): string => {
      const zeroBasedArrayIndex = state.ui.currentStep - 1;
      const fallBackStepName =
        state.ui._steps.at(0)?.name || CHECKOUT_STEP_1_NAME;

      const stepName =
        state.ui._steps.at(zeroBasedArrayIndex)?.name || fallBackStepName;

      return stepName;
    },
    isCartEmpty: (state: CheckoutStoreState) => {
      return state.fetchedData.cart.cartItems.length === 0;
    },
    isKlarnaPaymentMethodSelected: (state: CheckoutStoreState) => {
      return (
        state.userState.payment.selectedPaymentMethodCommaSeparatedIdentifier &&
        state.userState.payment.selectedPaymentMethodCommaSeparatedIdentifier
          .toLowerCase()
          .includes('klarna')
      );
    },
    isKlarnaPartialPaymentMethodSelected: (state: CheckoutStoreState) => {
      return (
        state.userState.payment
          .selectedPaymentMethodCommaSeparatedIdentifier ===
        KLARNA_PARTPAYMENT_IDENTIFIER
      );
    },
    payment_directPaymentSumToDisplay: (state: CheckoutStoreState): number => {
      return state.fetchedData.cart?.subTotals?.SubTotalWithDiscount ?? 0;
    },
    payment_partialPaymentSumToDisplay: (
      state: CheckoutStoreState,
    ): number | undefined => {
      // should calculation of combined price be a data point from the backend?

      const cartItems = state.fetchedData?.cart?.cartItems;
      if (!cartItems || !cartItems.length) {
        return 0;
      }

      const selectedDownPayTime = state.userState.payment.initialDownpayTime;
      const hardwareDownPayment =
        state.fetchedData.cart.subTotals.Payments.find(
          (payment) => payment.DownPayments === selectedDownPayTime,
        )?.HardwareMonthlyPayment;
      return hardwareDownPayment;
    },
    shippingMethodSelection: (state: CheckoutStoreState) => {
      return (
        state.userState.selectedDeliveryMethod?.Name +
        '___' +
        state.userState.selectedDeliveryMethod?.ShippingMethodSystemName
      );
    },
    selectedPickupLocation: (
      state: CheckoutStoreState,
    ): ShippingLockers | undefined => {
      return state.userState.selectedDeliveryMethod?.ServicePoints?.find(
        (point) => point.Selected === true,
      );
    },
    steps: (state: CheckoutStoreState): StepIndicatorStep[] => {
      return state.ui._steps;
    },
    teliaPayToken: (state: CheckoutStoreState) => {
      return state.ui.teliaPayReady && state.fetchedData._teliaPayToken
        ? state.fetchedData._teliaPayToken
        : '';
    },
    trackingData(): TrackingData {
      const items = this.fetchedData.cart.cartItems.flatMap((item) =>
        tracking_mapCartItemModelToEcommerceItem(
          item,
          this.fetchedData.selectedDownPayment,
        ),
      );

      const trackingData: TrackingData = {
        ecommerce: {
          items: items,
        },
        selectedPayment:
          this.userState.payment.selectedPaymentMethodCleanName || '',
        selectedShipping: this.userState.selectedDeliveryMethod?.Name,
        numberOptions: Object.keys(this.userState.numberConfigMap).map(
          (key) => {
            return {
              item_id: this.userState.numberConfigMap[key].cartItemId,
              e_sim: this.userState.numberConfigMap[key].esim,
              hidden_number: this.userState.numberConfigMap[key].hiddenNumber,
              number_selection:
                this.userState.numberConfigMap[key].keepNumberOption,
            };
          },
        ),
      };
      return trackingData;
    },
  },
});
